import React, {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import signalEvent from 'infrastructure/signalR/signalEvents';
import { getAccessToken } from 'infrastructure/security/accessTokenManager';
import useCurrentUser from 'infrastructure/currentUser/useCurrentUser';
import { createSignalRLogger, redactSignalRLogMessage } from './signalRLogger';

export interface ISignalRContext {
  connection: MutableRefObject<signalR.HubConnection | undefined>;
  connectionVersion: number;
  listenForSignalEvent: (
    eventName: SignalEventName,
    callback: SignalEventCallback
  ) => void;
  stopListeningForTheSignalEvent: (eventName: SignalEventName) => void;
  signalEvent: Record<string, string>;
}

export const SignalRContext = createContext<ISignalRContext>(
  {} as ISignalRContext
);

export type SignalEventName = string;
export type SignalEventCallback = (data: any) => void;

const SignalRContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const connection = useRef<signalR.HubConnection>();
  const [connectionVersion, setConnectionVersion] = useState(0);
  const { currentUser } = useCurrentUser();
  const currentUserId = currentUser?.id;
  const hasAccessToken = !!getAccessToken();
  const signalRLogger = useMemo(
    () => createSignalRLogger(process.env.SIGNALR_LOG_LEVEL),
    []
  );

  /**
   * This public method is binding the .on() callback on SignalR's connection
   * object. Docs ref: https://docs.microsoft.com/pl-pl/aspnet/core/signalr/javascript-client?view=aspnetcore-6.0#call-client-methods-from-the-hub
   * @param eventName
   * @param callback
   */
  const listenForSignalEvent = useCallback(
    (eventName: SignalEventName, callback: SignalEventCallback) => {
      if (!connection.current) {
        return;
      }
      connection.current.on(eventName, callback);
    },
    []
  );

  /**
   * Undocumented method of SignalR but exists in source code.
   * GitHub ref: https://github.com/SignalR/SignalR/blob/f0a1b5bdb891601e730a7d0ea70cd85d7a09e527/src/Microsoft.AspNet.SignalR.JS/jquery.signalR.hubs.js#L158
   * @param eventName
   */
  const stopListeningForTheSignalEvent = useCallback(
    (eventName: SignalEventName) => {
      if (!connection.current) {
        return;
      }
      connection.current.off(eventName);
    },
    []
  );

  /**
   * This effect is responsible for creating SignalR Hub object, establishing a connection and binding all of the
   * callbacks being used in application.
   */
  useEffect(() => {
    if (!currentUserId || !hasAccessToken) {
      if (!connection.current) {
        return undefined;
      }

      const existingConnection = connection.current;
      connection.current = undefined;
      setConnectionVersion(version => version + 1);

      existingConnection.stop().catch(error => {
        console.error(redactSignalRLogMessage(error.message));
      });
      return undefined;
    }

    const finalURL = new URL(process.env.SIGNALR_HUB_NAME, process.env.API_URL)
      .href;

    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(finalURL, { accessTokenFactory: () => getAccessToken() || '' })
      .withAutomaticReconnect()
      .withStatefulReconnect()
      .configureLogging(signalRLogger)
      .build();

    connection.current = hubConnection;
    setConnectionVersion(version => version + 1);

    const onConnected = () => {
      hubConnection.invoke('AddToGroup').catch(error => {
        console.error(redactSignalRLogMessage(error.message));
      });
    };

    let isActiveConnection = true;

    hubConnection
      .start()
      .then(() => {
        if (isActiveConnection) {
          onConnected();
        }
      })
      .catch(error => {
        console.error(redactSignalRLogMessage(error.message));
        if (isActiveConnection && connection.current === hubConnection) {
          connection.current = undefined;
          setConnectionVersion(version => version + 1);
        }
      });

    hubConnection.onreconnected(() => {
      onConnected();
    });

    return () => {
      isActiveConnection = false;

      if (connection.current === hubConnection) {
        connection.current = undefined;
        setConnectionVersion(version => version + 1);

        hubConnection.stop().catch(error => {
          console.error(redactSignalRLogMessage(error.message));
        });
      }
    };
  }, [currentUserId, hasAccessToken, signalRLogger]);

  const contextValue = useMemo(
    () => ({
      connection,
      connectionVersion,
      listenForSignalEvent,
      stopListeningForTheSignalEvent,
      signalEvent,
    }),
    [connectionVersion, listenForSignalEvent, stopListeningForTheSignalEvent]
  );

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};

export default SignalRContextProvider;
